import {zodResolver} from '@hookform/resolvers/zod';
import {UserGroup} from '@nursio-link/common/model/User';
import dayjs from 'dayjs';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {useGroupAuthorize} from '@/hooks/useAuth';
import {useCreateEvents} from '@/hooks/useEvents';
import type {Event} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {EventFormValues} from '../utils/eventsFormInputs';
import {
  eventFormDefaultValues,
  eventFormSchema,
  mapEventFormValuesToCreateInputs,
} from '../utils/eventsFormInputs';
import EventFormFields from './EventFormFields';

export type AddEventDialogProps = DialogProps;

const AddEventDialog = ({onOpenChange, ...props}: AddEventDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const createEvents = useCreateEvents();

  const isEmployee = useGroupAuthorize([
    UserGroup.Employees,
    UserGroup.EmployeesInventory,
  ]);
  const pairingId = searchParams.get('pairingId') || '';
  const selectedDate = dayjs(searchParams.get('date') || undefined).format(
    TIME_FORMATS.dateTimeHTMLLocal
  );

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: eventFormDefaultValues(isEmployee, {
      pairingId,
      date: selectedDate,
    } as Event),
  });

  useEffect(() => {
    if (pairingId) {
      form.setValue('pairingIds', [pairingId]);
    }
    if (selectedDate) {
      form.setValue('date', selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairingId, selectedDate]);

  const invalidateEventQueries = async () => {
    try {
      await queryClient.invalidateQueries({
        predicate: query =>
          ['GetEventsByPairingId', 'GetEventsByDateRange'].includes(
            String(query.queryKey[0])
          ),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (values: EventFormValues) => {
    try {
      await createEvents.mutateAsync(mapEventFormValuesToCreateInputs(values));
    } catch (error) {
      console.error(error);
      await invalidateEventQueries();
      toast({
        title: t(
          'Some events may have been added. Check the events list before trying again.'
        ),
        variant: 'destructive',
      });
      return;
    }

    await invalidateEventQueries();
    toast({
      title: t('Event Added'),
      variant: 'success',
    });
    closeDialog(true);
  };

  const closeDialog = (value: boolean) => {
    form.reset();
    onOpenChange?.(value);
  };

  const handleOpenChange = (value: boolean) => {
    if (form.formState.isSubmitting) return;

    closeDialog(value);
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Event')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              <EventFormFields form={form} multipleResidents />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={form.formState.isSubmitting}
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                {t('Close')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default AddEventDialog;
